import { Addon } from "./types";

export type PaymentType =
  | "kostenpflichtig"
  | "fremdabrechnung"
  | "beitragsfrei";

/**
 * Sums a membership figure across the currently selected payment types.
 *
 * `beitragsfrei` and `fremdabrechnung` are explicit shares of `base`; the
 * remainder is treated as `kostenpflichtig`. Only the shares whose payment
 * type is included in `typeFilter` contribute to the total.
 */
export function sumByPaymentType(
  {
    base,
    beitragsfrei,
    fremdabrechnung,
  }: { base: number; beitragsfrei?: number; fremdabrechnung?: number },
  typeFilter: readonly string[]
): number {
  const bf = beitragsfrei ?? 0;
  const fa = fremdabrechnung ?? 0;
  const kostenpflichtig = base - bf - fa;

  let total = 0;
  if (typeFilter.includes("kostenpflichtig")) total += kostenpflichtig;
  if (typeFilter.includes("fremdabrechnung")) total += fa;
  if (typeFilter.includes("beitragsfrei")) total += bf;
  return total;
}

/**
 * Maps a single addon's four lifecycle figures (active, activated, terminated,
 * termination) through `sumByPaymentType`, honouring the selected payment types.
 */
export function filterAddonShares(
  addon: Addon,
  typeFilter: readonly string[]
): {
  active: number;
  activated: number;
  terminated: number;
  termination: number;
} {
  return {
    active: sumByPaymentType(
      {
        base: addon.activeMemberships,
        beitragsfrei: addon.davonBeitragsfrei,
        fremdabrechnung: addon.davonFremdabrechnung,
      },
      typeFilter
    ),
    activated: sumByPaymentType(
      {
        base: addon.activatedMemberships,
        beitragsfrei: addon.activatedBeitragsfrei,
        fremdabrechnung: addon.activatedFremdabrechnung,
      },
      typeFilter
    ),
    terminated: sumByPaymentType(
      {
        base: addon.terminatedMemberships,
        beitragsfrei: addon.terminatedBeitragsfrei,
        fremdabrechnung: addon.terminatedFremdabrechnung,
      },
      typeFilter
    ),
    termination: sumByPaymentType(
      {
        base: addon.terminationMemberships,
        beitragsfrei: addon.terminationBeitragsfrei,
        fremdabrechnung: addon.terminationFremdabrechnung,
      },
      typeFilter
    ),
  };
}

/**
 * Aggregates every visible addon of a club into the totals and per-member /
 * per-activation ratios shown in the addon summary cards. Addons whose `id`
 * is listed in `addonList` are treated as hidden and skipped.
 */
export function addonSummary(
  club: { addons?: Addon[]; total?: { active?: number; activations?: number } },
  addonList: number[],
  typeFilter: string[]
): {
  allActiveMembers: number;
  allActivated: number;
  allTerminations: number;
  allTerminated: number;
  allInTestphase: number;
  addonsPerMember: number;
  addonsPerActivation: number;
} {
  const addons = club?.addons ?? [];

  let allActiveMembers = 0;
  let allActivated = 0;
  let allTerminations = 0;
  let allTerminated = 0;
  let allInTestphase = 0;

  for (const addon of addons) {
    if (addonList.includes(addon.id)) continue;
    const shares = filterAddonShares(addon, typeFilter);
    allActiveMembers += shares.active;
    allActivated += shares.activated;
    allTerminations += shares.termination;
    allTerminated += shares.terminated;
    allInTestphase += addon.davonTestphaseCancelled ?? 0;
  }

  const addonsPerMember = club?.total?.active
    ? allActiveMembers / club.total.active
    : 0;
  const addonsPerActivation = club?.total?.activations
    ? allActivated / club.total.activations
    : 0;

  return {
    allActiveMembers,
    allActivated,
    allTerminations,
    allTerminated,
    allInTestphase,
    addonsPerMember,
    addonsPerActivation,
  };
}
